import { GridApi } from '@mui/x-data-grid-pro';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { MutableRefObject, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ZodError } from 'zod';

import { useGridValidator } from '../../components/IntakeDataGrid/IntakeDataGrid.hooks';
import {
  SupplierContactData,
  supplierContactSelectionArraySchema,
} from '../../components/SupplierContactForm/SupplierContactForm.schema';
import { IntakeSupplierContactQuery, SvbrgFormStepType } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import {
  useExtractedRequirements,
  shouldPrePopulateFromRequirements,
} from '~/routes/IntakeDetail/utils/useExtractedRequirements';

import { apiToSupplier, gridDataToSupplier } from './IntakeRequestSupplierSelectionStep.util';

type UseNextMutationProps = {
  apiRef: MutableRefObject<GridApi>;
  session: any;
  next: (options: { variables: { data: any } }) => void;
  isLoading: boolean;
};

export const useNextMutation = ({ apiRef, session, next, isLoading }: UseNextMutationProps) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeRequestSupplierSelectionStep' });
  const { parse } = useGridValidator({
    apiRef,
    schema: supplierContactSelectionArraySchema,
  });
  const { onError } = useCustomSnackbar();

  const handleNext = useCallback(async () => {
    // Given the schema `supplierContactSelectionArraySchema` there must be exactly 1 entry
    // otherwise parse will throw an exception and the UI will break
    // `isValid` from `useGridValidator` would allow more graceful handling
    try {
      const rows = parse();
      await next({
        variables: {
          data: {
            chatSessionId: session.id,
            suppliers: rows.map(gridDataToSupplier),
          },
        },
      });
    } catch (error) {
      if (error instanceof ZodError) {
        // we use the first error -> invalid supplier to let the user know which one is wrong
        // NOTE: we do not have the name in the grid dataset as this is resolved via curiosity on the cell itself
        const firstError = error.issues[0];
        const index = (firstError.path[0] ?? 0) as number;
        onError(t('error.supplier', { position: index + 1 }));
      } else {
        // show general error
        onError(error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [next, onError, parse, session.id]);

  const handleSkip = useCallback(() => {
    next({
      variables: {
        data: {
          chatSessionId: session.id,
          //FIXME: This actually clears the entire list of suppliers right now
          suppliers: [],
        },
      },
    });
  }, [session, next]);

  return { handleNext, handleSkip, isLoading };
};

type UseSupplierResult = {
  quotedSupplierId: string | null;
  suppliers: SupplierContactData[];
};

export const useSupplierData = (
  data: IntakeSupplierContactQuery | undefined,
  sessionId?: string,
): UseSupplierResult => {
  // Get extracted requirements for suppliers
  const { hasData: hasExtractedData, data: extractedData } = useExtractedRequirements({
    chatSessionId: sessionId || '',
    stepType: SvbrgFormStepType.REQUEST_SUPPLIER_SELECTION,
    enabled: Boolean(sessionId),
  });

  return useMemo(() => {
    const existingSuppliers = data?.sevenbridges.chatSession.suppliers ?? [];

    // If we have existing suppliers, process them normally
    if (existingSuppliers.length > 0) {
      return existingSuppliers.reduce<UseSupplierResult>(
        (acc, supplier) => {
          const mappedSupplier = apiToSupplier(supplier);
          if (mappedSupplier.supplier.isQuoted) {
            acc.quotedSupplierId = mappedSupplier.supplier.internalId;
          } else {
            acc.suppliers.push(mappedSupplier);
          }
          return acc;
        },
        { quotedSupplierId: null, suppliers: [] },
      );
    }

    // Pre-populate from extracted requirements if no existing suppliers
    if (shouldPrePopulateFromRequirements(hasExtractedData, existingSuppliers)) {
      // extractedData is already pre-processed by the backend as an array of suppliers
      const prePopulatedSuppliers = Array.isArray(extractedData) ? extractedData : [];
      return { quotedSupplierId: null, suppliers: prePopulatedSuppliers };
    }

    // Fallback to empty suppliers
    return { quotedSupplierId: null, suppliers: [] };
  }, [data, hasExtractedData, extractedData]);
};
